import { deleteDoc, deleteField, getDoc, getDocs, limit, orderBy, query, serverTimestamp, setDoc, updateDoc, where, } from "firebase/firestore";
import { asItemId, between, jitter, err, newId, notFound, ok, } from "@pack-it-up/shared";
import { emptyCompletion } from "@pack-it-up/core";
import { toDomainItem, toItemDto } from "../mappers/index.js";
import { assignmentToDto } from "../mappers/common.js";
import { observeQuery, patchField, tagsPatch, toDomainError } from "./support.js";
export const makeItemRepo = (db, p) => {
    // Append order = a fractional index just after the current last sibling.
    //
    // Concurrency (Doc 02 §6): `between` is deterministic, so two clients appending at
    // the same moment read the same `last` and would compute the *identical* key. We
    // `jitter` the append key so they differ (still strictly after `last`, so the
    // ordering is preserved). No transaction/lock is used: serialising appends per
    // category would be a needless write bottleneck, and a jittered key plus Firestore's
    // implicit `__name__` (doc-id) tiebreak on equal orders gives a stable total order
    // across clients without one.
    // listId equality filter is required for the read rule to be provable on a query
    // (rules key items access on resource.data.listId).
    const appendOrder = async (listId, categoryId) => {
        const snap = await getDocs(query(p.items(), where("listId", "==", listId), where("categoryId", "==", categoryId), orderBy("order", "desc"), limit(1)));
        const last = snap.docs[0]?.get("order");
        return jitter(between((last ?? null), null));
    };
    return {
        observeItems: (listId) => observeQuery(query(p.items(), where("listId", "==", listId)), toDomainItem, "items"),
        async createItem(input) {
            try {
                const catSnap = await getDoc(p.category(input.categoryId));
                if (!catSnap.exists())
                    return err(notFound("category", input.categoryId));
                const listId = catSnap.get("parentId");
                const assignment = { kind: "everyone" };
                const id = newId();
                const item = {
                    id: asItemId(id),
                    categoryId: input.categoryId,
                    name: input.name,
                    quantity: input.quantity,
                    ...(input.description !== undefined ? { description: input.description } : {}),
                    ...(input.notes !== undefined ? { notes: input.notes } : {}),
                    ...(input.tags !== undefined && input.tags.length > 0 ? { tags: input.tags } : {}),
                    assignment,
                    completions: emptyCompletion(),
                    order: await appendOrder(listId, input.categoryId),
                };
                // listId is the denormalised per-list (trip/template) query key (Doc 02 §8) —
                // not a domain field.
                await setDoc(p.item(id), { ...toItemDto(item), listId });
                return ok(asItemId(id));
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        async updateItem(patch) {
            const out = {};
            patchField(out, "name", patch.name);
            patchField(out, "quantity", patch.quantity);
            patchField(out, "description", patch.description);
            patchField(out, "tags", tagsPatch(patch.tags));
            patchField(out, "notes", patch.notes);
            // Nested field path: only the cached flag is written, the rest of the
            // provenance record is left exactly as the import/sync wrote it (Doc 02 §7).
            patchField(out, "provenance.modifiedSinceImport", patch.modifiedSinceImport);
            try {
                await updateDoc(p.item(patch.itemId), out);
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        // Change assignment only. The completion record is independent (assignment is a view
        // over it — see core/usecases/completion.ts), so existing checks are left untouched.
        async setAssignment(itemId, assignment) {
            try {
                await updateDoc(p.item(itemId), { assignment: assignmentToDto(assignment) });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        // Writes only the single changed completion path (Doc 02 §5, Doc 03 §4). Shared, everyone
        // and specific all store per-participant entries, so there's one code path.
        async setCompletion(itemId, userId, done) {
            try {
                const path = `completions.byParticipant.${userId}`;
                await updateDoc(p.item(itemId), done ? { [path]: { done: true, checkedBy: userId, at: serverTimestamp() } } : { [path]: deleteField() });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        // T27 — same per-participant path shape as setCompletion, under `staging`.
        async setStaging(itemId, userId, done) {
            try {
                const path = `staging.byParticipant.${userId}`;
                await updateDoc(p.item(itemId), done ? { [path]: { done: true, checkedBy: userId, at: serverTimestamp() } } : { [path]: deleteField() });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        async moveItem(itemId, toCategoryId, order) {
            try {
                await updateDoc(p.item(itemId), { categoryId: toCategoryId, order });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        async deleteItem(itemId) {
            try {
                await deleteDoc(p.item(itemId));
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
    };
};
//# sourceMappingURL=item.repo.js.map